import { coordsOf, isOnline, type Opportunity } from '../../shared/opportunity';

/**
 * Travel feasibility between two events.
 *
 * The pain-points research names this as the one unoccupied space in the
 * market: existing tools plot pins, none of them tells you whether your 2pm in
 * the Financial District and your 3pm at the Javits Center are compatible.
 *
 * These are honest estimates, not routed directions. Each neighborhood gets a
 * centroid; we take the great-circle distance and apply a mode-appropriate
 * speed plus a fixed overhead for waiting, transfers and getting in and out of
 * buildings. Two frictions the research flags are folded into the overhead:
 * venue security screening, and the UN General Assembly street closures that
 * slow the East Side during exactly this week.
 *
 * The shape of this module is deliberately swappable — `estimateTravel` is the
 * only export the UI uses, so a Mapbox Matrix call can replace the math
 * without touching a component.
 */

export type TravelMode = 'walk' | 'bike' | 'transit' | 'ferry';

export interface TravelEstimate {
  minutes: number;
  mode: TravelMode;
  from: string;
  to: string;
  /**
   * True when at least one end had to be measured from a neighborhood centroid
   * rather than its own address.
   *
   * With both addresses geocoded this is a real distance between real points.
   * Without one, a venue given only as "Manhattan - Midtown" could be anywhere
   * inside it, and the estimate could be twenty minutes out either way. Worth
   * surfacing because the user can act on it: go and read the listing.
   */
  approximate: boolean;
}

/**
 * Rough centroids, [lat, lng]. Precision beyond ~2 decimals is not meaningful
 * here. Keyed by the neighborhood labels the backend serves.
 */
const CENTROIDS: Record<string, [number, number]> = {
  'Manhattan - Downtown': [40.72, -74.0],
  // Added when these areas were, and easy to forget: an area with no centroid
  // silently produces no travel estimate at all for any event without its own
  // coordinates — which is every event whose address is withheld.
  'Manhattan - Chelsea': [40.747, -74.0],
  'Manhattan - Nolita': [40.722, -73.995],
  'Manhattan - Midtown': [40.755, -73.98],
  'Manhattan - Uptown': [40.8, -73.96],
  Manhattan: [40.76, -73.98],
  'Brooklyn - Downtown Brooklyn': [40.694, -73.99],
  'Brooklyn - Fort Greene': [40.689, -73.974],
  'Brooklyn - Williamsburg': [40.714, -73.957],
  'Brooklyn - Greenpoint': [40.73, -73.951],
  'Brooklyn - Bushwick': [40.694, -73.921],
  'Brooklyn - Gowanus': [40.674, -73.989],
  'Brooklyn - Park Slope': [40.671, -73.98],
  'Brooklyn - Prospect Heights': [40.677, -73.968],
  'Brooklyn - Red Hook': [40.677, -74.009],
  'Brooklyn - Sunset Park': [40.655, -74.005],
  'Brooklyn - Navy Yard': [40.702, -73.971],
  Brooklyn: [40.678, -73.944],
  'Queens - Long Island City': [40.745, -73.949],
  'Queens - Astoria': [40.764, -73.923],
  'Queens - Jackson Heights': [40.755, -73.883],
  'Queens - Flushing': [40.768, -73.833],
  'Queens - Corona': [40.749, -73.86],
  'Queens - Forest Hills': [40.719, -73.845],
  'Queens - Jamaica': [40.702, -73.789],
  'Queens - Bellerose': [40.727, -73.716],
  'Queens - Far Rockaway': [40.605, -73.755],
  'Queens - Rockaway': [40.588, -73.815],
  Queens: [40.728, -73.794],
  Bronx: [40.837, -73.886],
  'Staten Island': [40.579, -74.151],
  'Governors Island': [40.689, -74.017],
  'Roosevelt Island': [40.762, -73.95],
  /**
   * Newark, for the flight home.
   *
   * The only place outside the city the app names, and it earns its place: a
   * personal event for a flight out of Newark Liberty is one of the few things
   * a Climate Week schedule genuinely has to plan travel *to*, and without a
   * centroid here the airport resolved to Manhattan — Downtown, nine miles
   * away and on the wrong side of a river.
   *
   * The neighborhood is listed before the borough so it wins a tie: the two
   * share a point, and `areaForCoords` keeps the first of equal distances.
   * There is no map outline for either — `build-areas.py` derives those from
   * the city's own neighborhood file, which stops at the state line — but
   * nothing draws one, because a personal event has no callout.
   */
  'New Jersey - Newark': [40.7357, -74.1724],
  'New Jersey': [40.7357, -74.1724],
};

const MILES_PER_DEGREE_LAT = 69.0;
const MILES_PER_DEGREE_LNG = 52.5; // at NYC's latitude

function milesBetween(a: [number, number], b: [number, number]): number {
  const dLat = (a[0] - b[0]) * MILES_PER_DEGREE_LAT;
  const dLng = (a[1] - b[1]) * MILES_PER_DEGREE_LNG;
  // Manhattan-ish correction: you cannot travel as the crow flies in a city.
  return Math.sqrt(dLat * dLat + dLng * dLng) * 1.25;
}

const isFerryOnly = (area: string) => area === 'Staten Island' || area === 'Governors Island';

/**
 * @returns null when both events are online, or when neither has a location we
 *          can reason about — better to say nothing than to invent a number.
 */
/**
 * Travel between two events, or null when the question does not arise.
 *
 * An online event has no location, so there is nothing to travel to or from.
 * This used to return ten minutes for "settling in somewhere", which put a
 * travel block on the schedule of someone who was not going anywhere. The
 * caller is responsible for the more useful reading: an online event in the
 * middle of the day leaves you wherever you already were, so the next journey
 * starts from the last *physical* place you actually stood. See `buildDayPlan`.
 */
/**
 * Where an event actually is, for measuring from.
 *
 * The geocoded address whenever there is one, and only otherwise the
 * neighborhood's centroid. Measuring two events at 333 W 23rd St from the
 * middle of Midtown put ten minutes of travel between a venue and itself.
 */
export function pointFor(event: Opportunity): [number, number] | null {
  const coords = coordsOf(event);
  if (coords) return coords;
  return event.neighborhood ? (CENTROIDS[event.neighborhood] ?? null) : null;
}

/**
 * Under this, two addresses are the same place. About eighty metres — a
 * building's own footprint, or two entrances to one complex.
 */
const SAME_PLACE_MILES = 0.05;

/** Two points close enough to be one place: the day's route map draws them as one stop. */
export const isSamePlace = (a: [number, number], b: [number, number]): boolean =>
  milesBetween(a, b) < SAME_PLACE_MILES;

export function estimateTravel(
  from: Opportunity,
  to: Opportunity,
  forceMode?: TravelMode,
): TravelEstimate | null {
  if (isOnline(from) || isOnline(to)) return null;

  const a = from.neighborhood;
  const b = to.neighborhood;
  if (!a || !b) return null;

  const fromLabel = shortArea(a);
  const toLabel = shortArea(b);

  const pa = pointFor(from);
  const pb = pointFor(to);
  if (!pa || !pb) return null;

  // Both ends geocoded means the distance is real rather than inferred.
  const exact = Boolean(coordsOf(from) && coordsOf(to));
  const miles = milesBetween(pa, pb);

  /**
   * Same address, so there is no journey — not a short one.
   *
   * Only decided on real coordinates. Two events sharing a *neighborhood*
   * centroid are not at the same address; they are two unknown points inside
   * the same few square miles, and the ten-minute walk below is the honest
   * answer for that.
   */
  if (exact && miles < SAME_PLACE_MILES) return null;

  if (!exact && a === b) {
    return { minutes: 10, mode: 'walk', from: fromLabel, to: toLabel, approximate: true };
  }
  const ferryOnly = isFerryOnly(a) || isFerryOnly(b);
  const mode = forceMode ?? suggestMode(miles, ferryOnly);

  return {
    minutes: minutesFor(mode, miles, a, b),
    mode,
    from: fromLabel,
    to: toLabel,
    approximate: !exact,
  };
}

/** The mode we would pick unprompted, given the distance. */
function suggestMode(miles: number, ferryOnly: boolean): TravelMode {
  if (ferryOnly) return 'ferry';
  if (miles < 0.7) return 'walk';
  if (miles < 2.5) return 'bike';
  return 'transit';
}

function minutesFor(mode: TravelMode, miles: number, a: string, b: string): number {
  let minutes: number;

  switch (mode) {
    case 'walk':
      minutes = Math.round(miles * 21 + 4);
      break;
    case 'bike':
      minutes = Math.round(miles * 6 + 8);
      break;
    case 'ferry':
      // Ferries run to a timetable, so the wait dominates the crossing.
      minutes = Math.round(30 + miles * 4);
      break;
    default:
      // ~11 mph door to door once waiting and transfers are counted.
      minutes = Math.round(miles * 5.5 + 12);
  }

  // Midtown East sits inside the UNGA security perimeter this week.
  const eastSide = (area: string) => area === 'Manhattan - Midtown';
  if (eastSide(a) || eastSide(b)) minutes += 5;

  return Math.max(minutes, 5);
}

export interface ModeOption {
  mode: TravelMode;
  minutes: number;
}

/**
 * Every mode worth offering for this journey, in the order the dialog shows
 * them, each with what it would actually cost.
 *
 * Ferry appears only where one end is reachable no other way — offering it for
 * a trip between two Manhattan neighbourhoods would be noise. Conversely, where
 * the ferry *is* the only way, walking and cycling are the noise, so they are
 * dropped instead.
 */
export function travelOptions(from: Opportunity, to: Opportunity): ModeOption[] {
  if (isOnline(from) || isOnline(to)) return [];

  const a = from.neighborhood;
  const b = to.neighborhood;
  if (!a || !b) return [];

  const pa = pointFor(from);
  const pb = pointFor(to);
  if (!pa || !pb) return [];

  const exact = Boolean(coordsOf(from) && coordsOf(to));
  const miles = milesBetween(pa, pb);

  if (exact && miles < SAME_PLACE_MILES) return [];
  if (!exact && a === b) return [{ mode: 'walk', minutes: 10 }];
  const modes: TravelMode[] =
    isFerryOnly(a) || isFerryOnly(b) ? ['ferry', 'transit'] : ['walk', 'bike', 'transit'];

  return modes.map((mode) => ({ mode, minutes: minutesFor(mode, miles, a, b) }));
}

/** "Manhattan - Midtown" -> "Midtown"; "Brooklyn" stays "Brooklyn". */
export function shortArea(area: string): string {
  const parts = area.split(' - ');
  return parts.length > 1 ? parts[1] : parts[0];
}

export const MODE_LABEL: Record<TravelMode, string> = {
  walk: 'Based on time required for walking',
  bike: 'Based on time required for bike',
  transit: 'Based on time required for transit',
  ferry: 'Based on time required for ferry',
};

/** Just the mode, for places too narrow for the full sentence. */
export const MODE_NAME: Record<TravelMode, string> = {
  walk: 'walking',
  bike: 'bike',
  transit: 'transit',
  ferry: 'ferry',
};

/**
 * "45 min", "1 hr 23 min", "2 hr".
 *
 * Past an hour, minutes stop being a quantity anyone can feel: "83 min" has to
 * be divided before it means anything, and a schedule is read at a glance.
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

/**
 * The neighborhood a coordinate falls in, by nearest centroid.
 *
 * Used to give a personal event an area, which is what lets travel time be
 * calculated to and from it. Nearest-centroid rather than a point-in-polygon
 * test against the real boundaries: the same centroids are what every distance
 * in this module is measured between, so an address that resolves to the
 * nearest one is consistent with how it will then be used.
 */
export function areaForCoords([lat, lng]: [number, number]): string | null {
  let best: string | null = null;
  let bestMiles = Infinity;

  for (const [area, point] of Object.entries(CENTROIDS)) {
    const miles = milesBetween([lat, lng], point);
    if (miles < bestMiles) {
      bestMiles = miles;
      best = area;
    }
  }

  // Beyond this the point is not in any neighborhood we know, and claiming one
  // would put a pin in the wrong borough rather than admit ignorance.
  return bestMiles <= 12 ? best : null;
}
